import {
  CRMChannel,
  TwilioConversationModel,
  WebChatBotConversationModel,
  PlanTierIndex,
} from "@jugl-web/rest-api";
import { InstagramConversationModel } from "@jugl-web/rest-api/integrations/models/InstagramConversation";
import { WhatsappConversationModel } from "@jugl-web/rest-api/integrations/models/WhatsappConversation";
import {
  InteractiveContainer,
  MultiSectionLayout,
} from "@jugl-web/ui-components";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { FC, useEffect, useRef, useState, useMemo } from "react";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { getPlanTierIndex } from "@web-src/modules/cx/pages/CXPlansPage/utils/planTierUtils";
import { hasVisitorIntelligenceData } from "@web-src/modules/contacts/ContactsPage/utils";
import { VisitorsIntelligenceDialog } from "@web-src/modules/contacts/ContactsPage/components/VisitorsIntelligenceDialog";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { useClientSupportPageContext } from "../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useWhatsappAccount } from "../../providers/WhatsappAccountProvider/useWhatsappAccount";
import { WebChatAvatar } from "../WebChatAvatar";
import { ReactComponent as InstagramIcon } from "./assets/instagram.svg";
import { ReactComponent as ScheduleIcon } from "./assets/schedule.svg";
import { ReactComponent as SmsIcon } from "./assets/sms.svg";
import { ReactComponent as WebIcon } from "./assets/web.svg";
import { ReactComponent as MingcuteIcon } from "./assets/mingcute.svg";
import { ReactComponent as WhatsappIcon } from "./assets/whatsapp.svg";
import { ConversationInfoSidebar } from "./components/ConversationInfoSidebar";
import { InstagramChatInput } from "./components/InstagramChatInput";
import {
  InstagramChatMessageWindow,
  InstagramChatMessageWindowHandle,
} from "./components/InstagramChatMessageWindow";
import { SelectChatSection } from "./components/SelectChatSection";
import { TwilioChatInput } from "./components/TwilioChatInput";
import {
  TwilioChatMessageWindow,
  TwilioChatMessageWindowHandle,
} from "./components/TwilioChatMessageWindow";
import { WebChatInput } from "./components/WebChatInput";
import {
  WebChatMessageWindow,
  WebChatMessageWindowHandle,
} from "./components/WebChatMessageWindow";
import { WhatsappChatInput } from "./components/WhatsappChatInput/WhatsappChatInput";
import {
  WhatsappChatMessageWindow,
  WhatsappChatMessageWindowHandle,
} from "./components/WhatsappChatMessageWindow/WhatsappChatMessageWindow";
import { formatWhatsappPhoneNumber } from "./utils/formatWhatsappPhoneNumber";
import { getChatSecondsToExpiration } from "./utils/getChatSecondsToExpiration";
import { ConversationContactMenu } from "./components/ConversationContactMenu";

const ClientSupportChatInner: FC<{
  selectedConversation:
    | WhatsappConversationModel
    | InstagramConversationModel
    | WebChatBotConversationModel
    | TwilioConversationModel;
}> = ({ selectedConversation }) => {
  const [isDetailsSideBarOpen, setIsDetailsSideBarOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const { isCx } = useAppVariant();

  const { currentPlan } = useSubscriptionV2();
  const { isAdmin } = useUserRole();

  const hasBusinessPlanOrAbove = useMemo(
    () =>
      currentPlan && getPlanTierIndex(currentPlan) >= PlanTierIndex.business,
    [currentPlan]
  );

  const { whatsappAgent } = useWhatsappAccount();
  const { selectedChannel, setOpenedContactInfo } =
    useClientSupportPageContext();
  const isWhatsappChannel = selectedChannel === CRMChannel.whatsapp;
  const isWebChannel = selectedChannel === CRMChannel.chatbot;
  const isInstagramChannel = selectedChannel === CRMChannel.instagram;
  const isTwilioChannel = selectedChannel === CRMChannel.twilio;

  const messageWindowRef = useRef<WhatsappChatMessageWindowHandle>(null);
  const instagramMessageWindowRef =
    useRef<InstagramChatMessageWindowHandle>(null);
  const webMessageWindowRef = useRef<WebChatMessageWindowHandle>(null);
  const twilioMessageWindowRef = useRef<TwilioChatMessageWindowHandle>(null);
  const { t } = useTranslations();

  const hasVisitorIntelligence = useMemo(
    () => hasVisitorIntelligenceData(selectedConversation.contact_info || {}),
    [selectedConversation]
  );

  const isContactBlocked =
    typeof selectedConversation.payload === "object" &&
    selectedConversation.payload !== null &&
    "action" in selectedConversation.payload &&
    selectedConversation.payload.action === "blocked";

  const isChatExpired =
    secondsLeft <= 0 && (isWhatsappChannel || isInstagramChannel);

  const formattedTimer = `${String(
    Math.floor((secondsLeft / 3600) % 24)
  ).padStart(2, "0")}:${String(Math.floor((secondsLeft / 60) % 60)).padStart(
    2,
    "0"
  )}:${String(secondsLeft % 60).padStart(2, "0")}`;

  useEffect(() => {
    if ("since" in selectedConversation) {
      setSecondsLeft(getChatSecondsToExpiration(selectedConversation.since));
    }
  }, [selectedConversation]);

  useEffect(() => {
    if (!("since" in selectedConversation)) {
      setSecondsLeft(0);
      return undefined;
    }

    const timer = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : prev));
    }, 1000);

    return () => clearInterval(timer);
  }, [selectedConversation]);

  return (
    <>
      <MultiSectionLayout
        header={
          <>
            <div
              className={cx(
                "font-secondary flex items-center justify-between gap-4 px-10 py-2",
                ((isWhatsappChannel && !isCx) || isCx) && "cursor-pointer"
              )}
              onClick={
                isWhatsappChannel && !isCx
                  ? () => setIsDetailsSideBarOpen(true)
                  : isCx
                  ? () =>
                      setOpenedContactInfo({
                        conversationId: selectedConversation.conv_id,
                        variant: "info",
                      })
                  : undefined
              }
              style={{ boxShadow: "0px 0px 40px 0px rgba(0, 0, 0, 0.08)" }}
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-solid border-white">
                  {isWhatsappChannel ? (
                    <WhatsappIcon />
                  ) : isWebChannel ? (
                    <WebChatAvatar
                      conversationName={
                        selectedConversation.contact_info?.name ||
                        selectedConversation.name
                      }
                      size="md"
                    />
                  ) : isInstagramChannel ? (
                    <InstagramIcon />
                  ) : isTwilioChannel ? (
                    <SmsIcon />
                  ) : (
                    <WebIcon />
                  )}
                </div>
                <div>
                  <div className="leading-2 mb-[2px] text-sm font-bold">
                    {selectedConversation.contact_info?.name ||
                      selectedConversation.name}
                  </div>
                  <div className="leading-2 font-secondary flex items-center gap-1 text-[13px] text-[#9E9E9E]">
                    {isWhatsappChannel
                      ? formatWhatsappPhoneNumber(
                          selectedConversation.conv_id,
                          whatsappAgent?.settings.hide_num || false
                        )
                      : isWebChannel
                      ? `Chat ID: ${selectedConversation.conv_id}`
                      : null}
                    {(isWhatsappChannel || isWebChannel) &&
                      isWebChannel &&
                      hasBusinessPlanOrAbove &&
                      hasVisitorIntelligence &&
                      isCx && (
                        <div className="flex w-[20px] justify-center">•</div>
                      )}
                    {hasBusinessPlanOrAbove &&
                      hasVisitorIntelligence &&
                      isWebChannel &&
                      isCx && (
                        <InteractiveContainer
                          className="flex items-center gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenedContactInfo({
                              conversationId: selectedConversation.conv_id,
                              variant: "visitorIntelligence",
                            });
                          }}
                        >
                          <MingcuteIcon className="shrink-0" />
                          <span className="text-primary-900 font-medium underline">
                            {t({
                              id: "client-support-page.visitor-intelligence",
                              defaultMessage: "Visitor Intelligence",
                            })}
                          </span>
                        </InteractiveContainer>
                      )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {!hasBusinessPlanOrAbove && isCx && isWebChannel && isAdmin && (
                  <VisitorsIntelligenceDialog size="small" />
                )}
                {secondsLeft > 0 && !isContactBlocked && (
                  <div className="flex items-center gap-4">
                    <div
                      className="bg-primary-700 font-secondary flex h-8 shrink-0 cursor-default items-center gap-2.5 whitespace-nowrap rounded-lg px-4 text-[13px] leading-[150%] text-white"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-2">
                        <ScheduleIcon />
                        <span>
                          {t({
                            id: "client-support-page.time-left",
                            defaultMessage: "Time left",
                          })}
                          :
                        </span>
                      </div>
                      <span className="font-medium">{formattedTimer}</span>
                    </div>
                  </div>
                )}
                {isCx && isWhatsappChannel && (
                  <ConversationContactMenu
                    isChatExpired={isChatExpired}
                    isContactBlocked={isContactBlocked}
                  />
                )}
              </div>
            </div>
          </>
        }
        footer={
          !isContactBlocked ? (
            isWebChannel ? (
              <WebChatInput
                chatId={selectedConversation.conv_id}
                conversationName={selectedConversation.name}
                onMessageSent={() => {
                  setTimeout(() => {
                    webMessageWindowRef.current?.scrollToBottom();
                  }, 100);
                }}
              />
            ) : isTwilioChannel ? (
              <TwilioChatInput
                chatId={selectedConversation.conv_id}
                onMessageSent={() => {
                  setTimeout(() => {
                    twilioMessageWindowRef.current?.scrollToBottom();
                  }, 100);
                }}
              />
            ) : isInstagramChannel ? (
              <InstagramChatInput
                chatId={selectedConversation.conv_id}
                isChatExpired={isChatExpired}
                onMessageSent={() => {
                  setTimeout(() => {
                    instagramMessageWindowRef.current?.scrollToBottom();
                  }, 100);
                }}
              />
            ) : (
              <WhatsappChatInput
                isChatExpired={isChatExpired}
                chatId={selectedConversation.conv_id}
                onMessageSent={() => {
                  setTimeout(() => {
                    messageWindowRef.current?.scrollToBottom();
                  }, 100);
                }}
              />
            )
          ) : null
        }
      >
        {isWhatsappChannel ? (
          <WhatsappChatMessageWindow
            ref={messageWindowRef}
            conversationId={selectedConversation.conv_id}
          />
        ) : isWebChannel ? (
          <WebChatMessageWindow
            ref={webMessageWindowRef}
            conversationId={selectedConversation.conv_id}
          />
        ) : isTwilioChannel ? (
          <TwilioChatMessageWindow
            ref={twilioMessageWindowRef}
            conversationId={selectedConversation.conv_id}
          />
        ) : (
          <InstagramChatMessageWindow
            ref={instagramMessageWindowRef}
            conversationId={selectedConversation.conv_id}
          />
        )}
        {!isCx && isWhatsappChannel && (
          <ConversationInfoSidebar
            isOpen={isDetailsSideBarOpen}
            onClose={() => setIsDetailsSideBarOpen(false)}
            isContactBlocked={isContactBlocked}
            isChatExpired={isChatExpired}
          />
        )}
      </MultiSectionLayout>
    </>
  );
};

export const ClientSupportChat: FC = () => {
  const { selectedConversation } = useClientSupportPageContext();

  if (!selectedConversation) {
    return <SelectChatSection />;
  }
  return <ClientSupportChatInner selectedConversation={selectedConversation} />;
};
